import { useEffect } from 'react';

import { Box, Divider, Stack, Typography, tokens } from '@cast/design-system';
import { Cloud } from '@cast/shared-types';

import { AgentCodeSnippet } from '@oneclick/components/code/code';
import { useReliabilityDataScriptQuery } from '@oneclick/hooks/queries/inventory';

import { Permissions } from './_components/Permissions';
import { ProviderSelect } from './_components/ProviderSelect';
import { StepBadge } from './StepBadge';
import {
  OnScriptStateChange,
  ONBOARDING_STEP_COPY,
  SNAPSHOT_COMING_SOON_PROVIDERS,
} from '../../../_constants';

type SnapshotProps = {
  onScriptStateChange?: OnScriptStateChange;
};

export const Snapshot = ({ onScriptStateChange }: SnapshotProps) => {
  const provider = Cloud.aws;

  const { script, isLoading, error, refetch } = useReliabilityDataScriptQuery();

  useEffect(() => {
    onScriptStateChange?.({
      isLoading,
      hasError: !!error,
    });
  }, [isLoading, error, onScriptStateChange]);

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: '100%',
        pl: 20,
        pr: 24,
        pt: 24,
        gap: 0,
      }}
    >
      <Stack direction="row" gap={16} alignItems="flex-start">
        <StepBadge number={1} hasConnector />
        <Stack gap={16} flex={1} pb={24}>
          <Typography variant="h6" color={tokens.theme.fg.structural.primary}>
            {ONBOARDING_STEP_COPY.selectCloudProviderTitle}
          </Typography>
          <ProviderSelect
            selected={provider}
            onSelect={() => undefined}
            showAnywhere={false}
            disabledProviders={[...SNAPSHOT_COMING_SOON_PROVIDERS]}
            comingSoonProviders={[...SNAPSHOT_COMING_SOON_PROVIDERS]}
          />
        </Stack>
      </Stack>

      <Stack direction="row" gap={16} alignItems="flex-start">
        <StepBadge number={2} />
        <Stack gap={16} flex={1} pb={24}>
          <Stack gap={4}>
            <Typography variant="h6" color={tokens.theme.fg.structural.primary}>
              Run this script
            </Typography>
            <Typography
              variant="P14R"
              color={tokens.theme.fg.structural.secondary}
            >
              Run from the terminal with your CSP&apos;s cloud CLI configured.
              Uses read-only access — no IAM role is created or stored.
            </Typography>
          </Stack>
          <AgentCodeSnippet
            code={script}
            isLoading={isLoading}
            refetch={refetch}
            error={error}
            errorMessage="Failed to load snapshot script"
            testId="snapshot-script"
            skeletonHeight={120}
          />
        </Stack>
      </Stack>

      <Box sx={{ pl: 42, pb: 16 }}>
        <Divider />
      </Box>

      <Permissions
        permissions={[
          'No IAM role created or stored',
          '3 read-only permissions using your existing CLI credentials',
          'Cluster and node describe access only',
        ]}
      />
    </Stack>
  );
};
